import type { ZodType } from 'zod';

/**
 * A module's identity and the roles it offers. Imported by the CDK app, the
 * Lambda bundle, the SPA and the deploy tooling alike, so it stays free of
 * anything heavier than zod.
 */

/** The only role the core defines. Every other role belongs to a module. */
export const CORE_ADMIN = 'core:admin';

export type CoreRole = typeof CORE_ADMIN;

/** `ses:operator` — a module role as it is written everywhere outside its manifest. */
export type QualifiedRole<TId extends string, TRole extends string> = `${TId}:${TRole}`;

/** Everything a route or nav entry may require: this module's roles, plus `core:admin`. */
export type GrantableRole<TId extends string, TRole extends string> =
	QualifiedRole<TId, TRole> | CoreRole;

export interface ModuleRole<TName extends string = string> {
	/** Unqualified and kebab-case — `operator`, not `ses:operator`. */
	readonly name: TName;
	readonly title: string;
	readonly description: string;
	/**
	 * Roles this one subsumes, unqualified, applied transitively.
	 *
	 * A module's roles are usually written as a ladder — "everything above,
	 * plus…" — and saying so in prose while enforcing a flat set produces a
	 * role that is useless on its own: an `ses:admin` who was never also
	 * granted `ses:reader` could create an SMTP credential through the API and
	 * not list one, and saw no Email section at all.
	 *
	 * **Only within one module.** `core:admin` neither implies a module role
	 * nor is implied by one, because it manages people rather than resources —
	 * which is the distinction that would quietly disappear if implication
	 * crossed module lines.
	 */
	readonly includes?: readonly NoInfer<TName>[];
}

export interface NavEntry<TRole extends string = string> {
	/** Relative to the module's mount point. Empty string is the module's index. */
	readonly path: string;
	readonly title: string;
	readonly requiredRole: TRole;
}

export interface ModuleManifest<TId extends string = string, TRole extends string = string> {
	/** Stable and kebab-case. Namespaces routes, roles, tables and tags. */
	readonly id: TId;
	readonly title: string;
	readonly roles: readonly ModuleRole<TRole>[];
	/**
	 * `NoInfer` is load-bearing: without it the role union is inferred from the
	 * nav entries as well as from `roles`, so naming a role the module does not
	 * declare silently widens the union instead of failing to compile.
	 */
	readonly nav: readonly NavEntry<NoInfer<GrantableRole<TId, TRole>>>[];
	/**
	 * How the module behaves, edited in the app by an admin — never a deploy
	 * argument. Deploy arguments decide which modules exist; this decides how
	 * they act.
	 *
	 * **Every field needs a default.** A deployment nobody has configured must
	 * still produce a usable settings object, because that is the state every
	 * module starts in and most stay in — and because `D97` answers a stored
	 * object that no longer parses with the defaults, which only works if
	 * there are some. `defineManifest` checks it at import.
	 */
	readonly settings?: ZodType;
}

/**
 * Every role a manifest grants, qualified, as a type. A module declares its
 * roles once — in the manifest — and derives this for its API and UI surfaces
 * rather than restating the union and letting the two drift.
 */
export type RolesOf<TManifest> =
	TManifest extends ModuleManifest<infer TId, infer TRole> ? GrantableRole<TId, TRole> : never;

/**
 * Infers the id and role names from the literal passed in, so every other
 * surface can be typed against this module's own roles rather than `string`.
 */
export function defineManifest<const TId extends string, const TRole extends string>(
	manifest: ModuleManifest<TId, TRole>,
): ModuleManifest<TId, TRole> {
	/**
	 * At import, so a module whose settings cannot default fails the build
	 * rather than the first request that reads them — which would be in a
	 * deployment, on a page nobody could then open.
	 */
	if (manifest.settings !== undefined && !manifest.settings.safeParse({}).success) {
		throw new Error(
			`module '${manifest.id}': every settings field needs a default, so an unconfigured deployment still parses`,
		);
	}
	return manifest;
}

export function qualifyRole<TId extends string, TRole extends string>(
	id: TId,
	role: TRole,
): QualifiedRole<TId, TRole> {
	return `${id}:${role}`;
}

/** Every role this manifest grants, qualified, with `core:admin` appended. */
export function grantableRoles<TId extends string, TRole extends string>(
	manifest: ModuleManifest<TId, TRole>,
): GrantableRole<TId, TRole>[] {
	return [...manifest.roles.map((role) => qualifyRole(manifest.id, role.name)), CORE_ADMIN];
}

/**
 * Everything a holder of these roles effectively has, within this manifest.
 *
 * Qualified in and qualified out, so a caller works in the same namespace
 * everywhere. Roles belonging to other modules — and `core:admin` — pass
 * through untouched rather than being dropped: this widens a set, it does not
 * filter one.
 */
export function expandRoles(manifest: ModuleManifest, granted: Iterable<string>): Set<string> {
	const declared = new Map(manifest.roles.map((role) => [role.name, role.includes ?? []]));
	const effective = new Set<string>(granted);
	const prefix = `${manifest.id}:`;

	// A worklist rather than recursion: `includes` is transitive, and a cycle
	// in a malformed manifest would otherwise be a stack overflow at request
	// time rather than the refusal `assertManifestValid` already gives.
	const pending = [...effective]
		.filter((role) => role.startsWith(prefix))
		.map((role) => role.slice(prefix.length));

	while (pending.length > 0) {
		const name = pending.pop() as string;
		for (const included of declared.get(name) ?? []) {
			const qualified = `${prefix}${included}`;
			if (effective.has(qualified)) continue;
			effective.add(qualified);
			pending.push(included);
		}
	}

	return effective;
}

// Nested quantifiers, but each repetition must start with a literal hyphen, so
// backtracking is bounded — and the only inputs are ids written in this repo's
// own source and read at build time, never anything a request supplies.
// eslint-disable-next-line security/detect-unsafe-regex
const KEBAB_CASE = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;

export class ManifestInvalidError extends Error {
	readonly code = 'manifest_invalid';
}

/**
 * The checks the type system cannot make: casing, uniqueness, and nav entries
 * naming a role the module does not offer.
 *
 * Called by the core when it loads the enabled modules, so a malformed manifest
 * fails the deploy rather than producing a dashboard with an unreachable page.
 */
/** Depth-first, reporting the first cycle reachable from each role. */
function cyclesIn(manifest: ModuleManifest): string[][] {
	const declared = new Map(manifest.roles.map((role) => [role.name, role.includes ?? []]));
	const found: string[][] = [];
	const settled = new Set<string>();

	for (const role of manifest.roles) {
		const path: string[] = [];
		const onPath = new Set<string>();

		const walk = (name: string): boolean => {
			if (onPath.has(name)) {
				found.push([...path.slice(path.indexOf(name)), name]);
				return true;
			}
			if (settled.has(name)) return false;

			path.push(name);
			onPath.add(name);
			for (const included of declared.get(name) ?? []) {
				if (walk(included)) return true;
			}
			path.pop();
			onPath.delete(name);
			settled.add(name);
			return false;
		};

		walk(role.name);
	}

	return found;
}

export function assertManifestValid(manifest: ModuleManifest): void {
	const problems: string[] = [];

	if (!KEBAB_CASE.test(manifest.id)) {
		problems.push(`id '${manifest.id}' is not kebab-case`);
	}
	if (manifest.title.trim() === '') {
		problems.push('title is empty');
	}
	if (manifest.roles.length === 0) {
		problems.push('no roles declared — a module with no roles is unreachable');
	}

	const seen = new Set<string>();
	for (const role of manifest.roles) {
		if (!KEBAB_CASE.test(role.name)) {
			problems.push(`role '${role.name}' is not kebab-case`);
		}
		if (seen.has(role.name)) {
			problems.push(`role '${role.name}' is declared twice`);
		}
		seen.add(role.name);
	}

	const names = new Set(manifest.roles.map((role) => role.name));
	for (const role of manifest.roles) {
		for (const included of role.includes ?? []) {
			if (included === role.name) {
				problems.push(`role '${role.name}' includes itself`);
			} else if (!names.has(included)) {
				problems.push(`role '${role.name}' includes unknown role '${included}'`);
			}
		}
	}

	/**
	 * A cycle would make `expandRoles` depend on where it started, which is a
	 * role that means different things to different callers. Refused here so a
	 * malformed manifest fails the deploy rather than a request.
	 */
	for (const cycle of cyclesIn(manifest)) {
		problems.push(`roles form a cycle: ${cycle.join(' includes ')}`);
	}

	const grantable = new Set<string>(grantableRoles(manifest));
	const paths = new Set<string>();
	for (const entry of manifest.nav) {
		if (!grantable.has(entry.requiredRole)) {
			problems.push(`nav '${entry.path}' requires unknown role '${entry.requiredRole}'`);
		}
		if (paths.has(entry.path)) {
			problems.push(`nav path '${entry.path}' is declared twice`);
		}
		paths.add(entry.path);
	}

	if (problems.length > 0) {
		throw new ManifestInvalidError(`module '${manifest.id}': ${problems.join('; ')}`);
	}
}
